import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Briefcase, GraduationCap, Code, Users, BookOpen, Brain } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const AboutUs: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link 
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>

            <h1 className="text-3xl font-bold text-gray-900 mb-8">About Us</h1>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Rishikesh Joshi */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="aspect-w-4 aspect-h-3">
                  <img 
                    src="https://maitridesigns.com/wp-content/uploads/2021/05/RishikeshJoshi-MaitriDesigns-1.jpg" 
                    alt="Rishikesh Joshi"
                    className="object-cover w-full h-64"
                  />
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Rishikesh Joshi</h2>
                  <p className="text-[#7B4AE2] font-medium mb-4">Co-Founder | UX Design</p>
                  
                  <div className="space-y-4 text-gray-600">
                    <p>
                      Rishikesh has over 16+ years of experience in UI/UX. He is currently founding AI startup "Analyzthis" along with Anita Gandhi and 11thadmissions.com is a byproduct of the core logic they have together created which gives instant insights to its users.
                    </p>
                    
                    <div className="flex items-start">
                      <Briefcase className="h-5 w-5 text-[#7B4AE2] mt-1 mr-2 flex-shrink-0" />
                      <p>
                        He has worked with Autodesk, Infosys, Reliance Games, Vectorform, and Inoplexus. Loves, ideating, thinking and sketching.
                      </p>
                    </div>
                    
                    <div className="flex items-start">
                      <BookOpen className="h-5 w-5 text-[#7B4AE2] mt-1 mr-2 flex-shrink-0" />
                      <p>
                        Rishikesh was a visiting faculty at Flame University, MIT and Strate India. He also consults for various Tech companies in India.
                      </p>
                    </div>
                    
                    <div className="flex items-start">
                      <GraduationCap className="h-5 w-5 text-[#7B4AE2] mt-1 mr-2 flex-shrink-0" />
                      <p>
                        He has a masters in design from IDC – IIT Powai and bachelors in Mechanical Engineering from PVG, Pune.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Anita Gandhi */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="aspect-w-4 aspect-h-3">
                  <img 
                    src="https://flypaperplane.com/wp-content/uploads/2025/01/anita-gandhi.png" 
                    alt="Anita Gandhi"
                    className="object-cover w-full h-64"
                  />
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Anita Gandhi</h2>
                  <p className="text-[#7B4AE2] font-medium mb-4">Co-Founder | Engineering Lead</p>
                  
                  <div className="space-y-4 text-gray-600">
                    <p>
                      Driven engineering professional with a proven track record of planning, coordinating, and executing successful programs worth more than $20MM aligned to organizational objectives.
                    </p>
                    
                    <div className="flex items-start">
                      <Brain className="h-5 w-5 text-[#7B4AE2] mt-1 mr-2 flex-shrink-0" />
                      <p>
                        Deep technical expertise combined with an understanding of the importance of client engagement and satisfaction.
                      </p>
                    </div>
                    
                    <div className="flex items-start">
                      <Users className="h-5 w-5 text-[#7B4AE2] mt-1 mr-2 flex-shrink-0" />
                      <p>
                        Collaborative team leader continually focused on building relationships to produce highly effective teams, increasing productivity, and improving outcomes.
                      </p>
                    </div>
                    
                    <div className="flex items-start">
                      <Code className="h-5 w-5 text-[#7B4AE2] mt-1 mr-2 flex-shrink-0" />
                      <p>
                        Expertise includes JavaScript, Node.js, C#, HTML5/CSS3/SCSS, Java, PHP, TypeScript, Docker, GitHub, Bitbucket, CI/CD, Agile, SAFE Agile, AWS, and GCP.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Section */}
            <div className="bg-white rounded-lg shadow-sm p-8 mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 mb-6">
                At 11thadmissions.com, we're committed to simplifying the college admission process for students and their families. By combining our expertise in technology and education, we provide data-driven insights and personalized recommendations to help students make informed decisions about their academic future.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-4 bg-[#F0E9FF] rounded-lg">
                  <div className="w-12 h-12 bg-[#7B4AE2] rounded-lg flex items-center justify-center mb-4">
                    <Brain className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered Insights</h3>
                  <p className="text-gray-600">
                    Leveraging advanced algorithms to provide accurate predictions and recommendations.
                  </p>
                </div>
                
                <div className="p-4 bg-[#F0E9FF] rounded-lg">
                  <div className="w-12 h-12 bg-[#7B4AE2] rounded-lg flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Student Success</h3>
                  <p className="text-gray-600">
                    Helping students find the perfect college match for their academic journey.
                  </p>
                </div>
                
                <div className="p-4 bg-[#F0E9FF] rounded-lg">
                  <div className="w-12 h-12 bg-[#7B4AE2] rounded-lg flex items-center justify-center mb-4">
                    <BookOpen className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Data-Driven</h3>
                  <p className="text-gray-600">
                    Making decisions easier with comprehensive data and analytics.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;